import axios from "axios";
import styled from "@emotion/styled";
import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Switch,
  FormControlLabel,
  Pagination,
  Box,
  // Removed Select, MenuItem, InputLabel, FormControl as they are no longer needed for posts per page selection
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { paths } from "../../config/constants";

// --- START: usePostsPerPage Hook Definition ---
/**
 * Custom hook to manage the number of items displayed per page.
 * Defined internally within MyStaff.js
 *
 * @param {number} initialPostsPerPage - The initial number of posts to display per page.
 * @returns {{postsPerPage: number, setPostsPerPage: React.Dispatch<React.SetStateAction<number>>}}
 * An object containing the current `postsPerPage` and its setter function.
 */
const usePostsPerPage = (initialPostsPerPage = 10) => {
  const [postsPerPage, setPostsPerPage] = useState(initialPostsPerPage);
  return { postsPerPage, setPostsPerPage };
};
// --- END: usePostsPerPage Hook Definition ---


const PaperWrapper = styled(Paper)(() => ({
  ".partnertext": {
    fontFamily: "DM Sans",
    fontStyle: "normal",
    fontWeight: 700,
    fontSize: "32px",
    lineHeight: "42px",
    color: "#1F3648;",
    width: "100%",
  },
  ".drowpdown": {
    borderRadius: "5.86877px",
  },
}));

const TableCellWraper = styled(TableCell)(() => ({
  textAlign: "left",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "15px",
  lineHeight: " 20px",
  color: "#000000",
}));

export default function MyStaff() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const [bankInvoicelimit, setBankInvoiceLimit] = useState(0);
  const [isComissionNull,setIsComissionNull] = useState(null)
  const [accountType, setAccountType] = useState(null);
  const [role,setRole]=useState(null)
  let toastId1;
  let toastId2;
  let toastId3;
  // Use the custom hook for posts per page - value is now fixed here
  const { postsPerPage } = usePostsPerPage(10); // Fixed to 10 posts per page

  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.login);
  console.log("loggedInUser",loggedInUser)
  
  const fetchData = async () => {
      let obj = {
        userId: loggedInUser?.user?.data?._id,
      };
  
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_URL}api/user/getUserProfile`,
          obj
        );
        console.log(res?.data?.data);
        setIsProfileCompleted(res?.data?.data?.isProfileCompleted);
        setBankInvoiceLimit(res?.data?.data?.companyAgentLimit)
        setIsComissionNull(res?.data?.data?.companyAgentComission);
        setAccountType(res?.data?.data?.accountType);
        setRole(res?.data?.data?.role);
      } catch (error) {}
    };
  
    useEffect(() => {
        fetchData();
      }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_URL}api/user/fetchEmployees`,
        {
          companyName: loggedInUser?.user?.data?.companyName,
        }
      );
      setEmployees(data.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      toast.error("Failed to fetch employees.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Filter employees based on search query
  const filteredEmployees = employees.filter((employee) => {
    if (searchQuery === '') {
      return true;
    }
    return Object.values(employee).some((value) =>
      value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Calculate indices for slicing the filteredEmployees array
  const indexOfLastEmployee = currentPage * postsPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - postsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee);

  // Calculate total pages for the Pagination component based on filtered data
  const totalPages = Math.ceil(filteredEmployees.length / postsPerPage);

  // Handle pagination page change
  const handlePaginationClick = (event, value) => {
    setCurrentPage(value);
  };

  // onPostsPerPageChange and setPostsPerPage are no longer needed since the selection UI is removed.
  // The postsPerPage value is now solely controlled by the initial value passed to usePostsPerPage.

  const handleEdit = (employeeId) => {
    navigate(paths.company.EditStaff, { state: { employeeId } });
  };

  const handleToggleActive = (active, id) => {
    console.log("active, id",active, id)

    axios
      .post(
        `${process.env.REACT_APP_URL}api/user/${
          active ? "activeUser" : "deactiveUser"
        }`,
        {
          userId: id,
        }
      )
      .then(function (res) {
        fetchEmployees();
      })
      .catch(function (error) {
        console.log(error);
      });
  };



  return (
    <div className="compEmpPage">
      <PaperWrapper className="p-3">
        <div className="myStaffTop">
          <h3 className="mb-4">Employees</h3>
        <div className="flex justify-between mb-4 items-center">
          <Button variant="contained" onClick={(e) => {
    if (isProfileCompleted === false) {
      e.preventDefault();
      if (!toast.isActive(toastId1)) {
        toastId1 = toast.info(
          "You cannot book right now because your profile is incomplete.",
          { autoClose: 2500 }
        );
      }
      return;
    }

    if (isComissionNull === null) {
      e.preventDefault();
      if (!toast.isActive(toastId2)) {
        toastId2 = toast.info(
          "You cannot book right now because your commission has not yet been set. Contact customer support.",
          { autoClose: 2500 }
        );
      }
      return;
    }

    if (bankInvoicelimit == null) {
      e.preventDefault();
      if (!toast.isActive(toastId3)) {
        toastId3 = toast.info(
          "You cannot add employee right now because your bank invoice limit has not yet been set.",
          { autoClose: 2500 }
        );
      }
      return;
    }

    // ✅ Only navigate if all checks pass
    navigate(paths.company.companyRegisterStaff);
  }}>
            Add New Employee
          </Button>
       
          
        </div>

        <div className="d-flex align-items-center gap-3">
            {/* Search Input */}
            <TextField
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1); // Reset page on search
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search employees..."
            />

            {/* Removed Posts per page selector */}
          </div>
        </div>
        
        <div className="tableEmp-wrapper">
          <TableContainer className="responsiveEmpTable">
            <Table >
              <TableHead>
                <TableRow >
                  <TableCellWraper style={{ fontWeight: "700" }} >Employee Id</TableCellWraper>
                  <TableCellWraper style={{ fontWeight: "700" }} >First Name</TableCellWraper>
                  <TableCellWraper style={{ fontWeight: "700" }} >Last Name</TableCellWraper>
                  <TableCellWraper style={{ fontWeight: "700" }} >Email</TableCellWraper>
                  <TableCellWraper style={{ fontWeight: "700" }} >Status</TableCellWraper>
                  <TableCellWraper style={{ fontWeight: "700" }} >Actions</TableCellWraper>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Loading employees...
                    </TableCell>
                  </TableRow>
                ) : currentEmployees?.length > 0 ? (
                  currentEmployees.map((employee) => (
                    <TableRow key={employee._id}>
                      <TableCellWraper>{employee.id}</TableCellWraper>
                      <TableCellWraper>{employee.fName}</TableCellWraper>
                      <TableCellWraper>{employee.lName}</TableCellWraper>
                      <TableCellWraper>{employee.email}</TableCellWraper>
                       <TableCellWraper>
                                              <FormControlLabel
                                                control={
                                                  <Switch
                                                    checked={employee.isUserActivated === "1"}
                                                    onChange={(e) =>
                                                      handleToggleActive(
                                                        e.target.checked,
                                                        employee._id,
                                                      )
                                                    }
                                                    name="isUserActivated"
                                                    color="primary"
                                                  />
                                                }
                                                label={employee.isUserActivated === "1" ? "Activated" : "Not Activated"}
                                              />
                                            </TableCellWraper>
                      <TableCellWraper>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleEdit(employee._id)}
                        >
                          Edit
                        </Button>
                      </TableCellWraper>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No Employees Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination component */}
         <Box 
  className="my-4" 
  sx={{ 
    display: "flex", 
    justifyContent: "flex-end",  // replaces float-end
    backgroundColor: "#fff",     // ✅ fixed: 6 f’s was too many
    width: "100%"
  }}
>
  <Pagination
    count={totalPages}
    page={currentPage}
    onChange={handlePaginationClick}
    color="primary"
  />
</Box>
        </div>
      </PaperWrapper>
    </div>
  );
}